// src/modules/IntegratedMessaging/components/modals/ConfirmDeactivateModal.tsx
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { AlertTriangle } from 'lucide-react';

export interface ConfirmDeactivateModalRef {
  /**
   * Abre la modal de confirmación para desactivar una integración.
   * @param integrationName Nombre de la integración a desactivar.
   * @param onConfirm Callback que se ejecutará si el usuario acepta.
   */
  open: (integrationName: string, onConfirm: () => void) => void;
}

export const ConfirmDeactivateModal = forwardRef<ConfirmDeactivateModalRef>((_, ref) => {
  const { t } = useTranslation('integratedmessagingModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmRef = useRef<() => void>(() => {});
  const [integrationName, setIntegrationName] = useState('');

  useImperativeHandle(ref, () => ({
    open: (name: string, onConfirm: () => void) => {
      setIntegrationName(name);
      confirmRef.current = onConfirm;
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = () => {
    confirmRef.current();
    confirmRef.current = () => {};
    modalRef.current?.closeModal();
  };

  return (
    <ModalCenter ref={modalRef} title={t('modal.deactivate.title')} type="default" width="default">
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{t('modal.deactivate.warning', { name: integrationName })}</AlertDescription>
        </Alert>

        <p className="text-sm text-muted-foreground">{t('modal.deactivate.message')}</p>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={() => modalRef.current?.closeModal()}>
            {t('button.cancel')}
            <EscIcon className="w-4 h-4" />
          </Button>
          <Button variant="destructive" size="lg" onClick={handleConfirm}>
            {t('table.deactivate')}
            <EnterIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ConfirmDeactivateModal.displayName = 'ConfirmDeactivateModal';
